import React, { useMemo } from 'react';
import { SelectPicker, Tag } from 'rsuite';
import {
  formatPeriodStartLabel,
  getCurrentPayPeriod,
  getPayPeriodOptions,
  toDateOnlyString,
} from '../../utils/payPeriods';

// Shared pay-period dropdown. Renders the standard pay-period options and tags
// whichever one is the current period with a blue "Current" badge, both in the
// open menu and in the selected value. Value/onChange use the "start|end"
// date-only string produced by getPayPeriodOptions().
//
// `compact` shows the selected period as its start date only ("Starting Aug
// 01") so the closed picker fits a narrow filter row; the open menu still lists
// the full ranges either way.
//
// `flaggedValues` badges periods with `flagLabel` without stopping anybody
// choosing them. Opt-in and empty by default, so the pickers that only browse
// periods are unaffected.
//
// A badge rather than a disabled row on purpose. Selecting a period that has
// already been run is a reasonable thing to want - it is how somebody gets to
// that run - so the picker says what is there and lets the page offer the way
// through. Greying the row out answered a question nobody asked and left the
// only route to an existing run somewhere else entirely.
export default function PayPeriodSelect({
  value,
  onChange,
  data,
  currentValue,
  compact = false,
  flaggedValues = [],
  flagLabel = 'Already run',
  ...rest
}) {
  const options = useMemo(() => data || getPayPeriodOptions(), [data]);

  const flagged = useMemo(() => new Set(flaggedValues || []), [flaggedValues]);

  const currentPeriodValue = useMemo(() => {
    if (currentValue) return currentValue;
    const current = getCurrentPayPeriod();
    return `${toDateOnlyString(current.start)}|${toDateOnlyString(current.end)}`;
  }, [currentValue]);

  return (
    <SelectPicker
      data={options}
      value={value}
      onChange={onChange}
      cleanable={false}
      searchable={false}
      renderMenuItem={(label, item) => (
        <span className="d-flex align-items-center gap-2">
          <span>{label}</span>
          {item.value === currentPeriodValue && (
            <Tag color="blue" size="sm">Current</Tag>
          )}
          {flagged.has(item.value) && (
            <Tag color="orange" size="sm">{flagLabel}</Tag>
          )}
        </span>
      )}
      renderValue={(selected, item) => (
        <span className="d-flex align-items-center gap-2">
          <span>{compact ? (formatPeriodStartLabel(item?.start) || item?.label) : item?.label}</span>
          {selected === currentPeriodValue && (
            <Tag color="blue" size="sm">Current</Tag>
          )}
          {/* Badged when selected too, which is the case that matters most:
              the page opens on the period that just ended, and that is the one
              most likely to have been run already. */}
          {flagged.has(selected) && (
            <Tag color="orange" size="sm">{flagLabel}</Tag>
          )}
        </span>
      )}
      {...rest}
    />
  );
}
